// @ts-nocheck
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, X, Loader2, Upload, Copy, Lock, Globe } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';

const SUBDOMAIN_REASONS: Record<string, string> = {
  empty: 'Enter a subdomain',
  length: 'Must be 3–63 characters',
  format: 'Only lowercase letters, numbers, and hyphens',
  reserved: 'That subdomain is reserved',
  taken: 'Already taken',
};

const CREATE_ERRORS: Record<string, string> = {
  slug_taken: 'That slug is already in use. Try another.',
  subdomain_taken: 'That subdomain is already taken.',
  subdomain_reserved: 'That subdomain is reserved.',
  subdomain_format: 'Subdomain can only contain lowercase letters, numbers, and hyphens.',
  subdomain_length: 'Subdomain must be 3–63 characters.',
  not_authorized: 'You are not authorized to create organizations.',
  name_required: 'Organization name is required.',
  slug_required: 'Slug is required.',
};

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated?: () => void;
}

export function NewOrganizationDialog({ open, onOpenChange, onCreated }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [subdomain, setSubdomain] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [checkState, setCheckState] = useState<{ status: 'idle' | 'checking' | 'available' | 'error'; message?: string }>({ status: 'idle' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [privateAthletes, setPrivateAthletes] = useState(false);
  const [showEventsOnRecruitlook, setShowEventsOnRecruitlook] = useState(true);

  const debouncedSubdomain = useDebounce(subdomain, 450);

  useEffect(() => {
    if (!open) return;
    setName(''); setSlug(''); setSlugEdited(false); setSubdomain('');
    setAdminEmail(''); setAdminName(''); setPersonalMessage('');
    setLogoFile(null); setLogoPreview(null);
    setCheckState({ status: 'idle' }); setError(null); setMagicLink(null);
    setPrivateAthletes(false);
    setShowEventsOnRecruitlook(true);
  }, [open]);

  // auto-suggest slug from name unless manually edited
  useEffect(() => {
    if (!slugEdited) setSlug(slugify(name));
  }, [name, slugEdited]);

  // live subdomain validation (new org => p_org_id null)
  useEffect(() => {
    const check = async () => {
      const v = debouncedSubdomain.trim();
      if (!v) { setCheckState({ status: 'idle' }); return; }
      setCheckState({ status: 'checking' });
      try {
        const { data, error } = await (supabase as any).rpc('check_subdomain_available', {
          p_subdomain: v,
          p_org_id: null,
        });
        if (error) throw error;
        if (data?.available) setCheckState({ status: 'available', message: 'Available' });
        else setCheckState({ status: 'error', message: SUBDOMAIN_REASONS[data?.reason] || 'Not available' });
      } catch (e) {
        setCheckState({ status: 'error', message: 'Could not check availability' });
      }
    };
    check();
  }, [debouncedSubdomain]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) { toast.error('Please upload a PNG, JPG, SVG, or WebP image'); return; }
    if (file.size > MAX_LOGO_BYTES) { toast.error('Logo must be 2MB or smaller'); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const subdomainEmpty = useMemo(() => subdomain.trim() === '', [subdomain]);
  const canSubmit =
    name.trim().length > 0 &&
    slug.trim().length > 0 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(adminEmail) &&
    (subdomainEmpty || checkState.status === 'available') &&
    !submitting;

  const handleSubmit = async () => {
    setError(null);
    setMagicLink(null);
    if (!subdomainEmpty && checkState.status === 'error') {
      setError('Please fix the subdomain before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      // 1. create org
      const { data: createRes, error: createErr } = await (supabase as any).rpc('create_organization', {
        p_name: name.trim(),
        p_slug: slug.trim(),
        p_subdomain: subdomainEmpty ? null : subdomain.trim(),
        p_logo_url: null,
        p_private_athletes: privateAthletes,
        p_show_events_on_recruitlook: privateAthletes ? false : showEventsOnRecruitlook,
      });
      if (createErr) throw createErr;
      if (!createRes?.ok) {
        setError(CREATE_ERRORS[createRes?.error] || `Could not create organization (${createRes?.error || 'unknown error'})`);
        setSubmitting(false);
        return;
      }
      const orgId = createRes.org_id;

      // 2. upload logo (now that we have org id) + attach via save_org_branding
      if (logoFile) {
        try {
          const ext = (logoFile.name.split('.').pop() || 'png').toLowerCase();
          const path = `${orgId}/logo.${ext}`;
          const { error: upErr } = await supabase.storage
            .from('org-logos')
            .upload(path, logoFile, { cacheControl: '3600', upsert: true });
          if (upErr) throw upErr;
          const { data: urlData } = supabase.storage.from('org-logos').getPublicUrl(path);
          await (supabase as any).rpc('save_org_branding', {
            p_org_id: orgId,
            p_subdomain: null,
            p_logo_url: urlData.publicUrl,
            p_is_recruitlook_branded: false,
            p_clear_subdomain: false,
          });
        } catch (e: any) {
          toast.warning('Organization created, but the logo failed to upload. You can add it from the org page.');
        }
      }

      // 3. invite the org admin
      const { data: inviteData, error: inviteErr } = await (supabase as any).functions.invoke('rle-invite-admin', {
        body: {
          invitee_email: adminEmail.trim(),
          invitee_name: adminName.trim() || null,
          role: 'rlevents_tenant_admin',
          organization_id: orgId,
          personal_message: personalMessage.trim() || null,
        },
      });

      if (inviteErr || !inviteData?.ok) {
        toast.success('Organization created');
        toast.warning('But the admin invite could not be sent. Invite them later from the org page.');
        onCreated?.();
        onOpenChange(false);
        return;
      }

      const d = inviteData.data || {};
      if (d.email_sent) {
        toast.success(`Organization created, invite sent to ${adminEmail.trim()}`);
        onCreated?.();
        onOpenChange(false);
      } else {
        // surface manual link
        toast.warning('Organization created, but the invite email failed to send.');
        setMagicLink(d.magic_link ?? null);
        setError(d.email_error ? `Email error: ${d.email_error}` : 'Email delivery failed — share the link below manually.');
        onCreated?.();
      }
    } catch (e: any) {
      setError(e.message || 'Something went wrong creating the organization.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle>New organization</DialogTitle>
          <DialogDescription>
            Create a white-label client organization and invite its admin.
          </DialogDescription>
        </DialogHeader>

        {magicLink ? (
          <div className="space-y-3">
            <Alert>
              <AlertDescription>
                The organization was created, but the invite email failed. Share this link manually:
              </AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Input value={magicLink} readOnly className="text-xs" />
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(magicLink); toast.success('Link copied'); }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <Button className="w-full" onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Organization name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Hoops" />
            </div>

            <div>
              <Label>Slug *</Label>
              <Input
                value={slug}
                onChange={(e) => { setSlugEdited(true); setSlug(slugify(e.target.value)); }}
                placeholder="acme-hoops"
                autoCapitalize="none"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground mt-1">Auto-suggested from the name. Used internally to identify the org.</p>
            </div>

            <div>
              <Label>Subdomain <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex items-stretch">
                <Input
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                  placeholder="acme"
                  className="rounded-r-none"
                  autoCapitalize="none"
                  spellCheck={false}
                />
                <span className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-input bg-muted text-sm text-muted-foreground whitespace-nowrap">
                  .recruitlookevents.com
                </span>
              </div>
              <div className="min-h-[20px] mt-1 text-sm">
                {checkState.status === 'checking' && (
                  <span className="flex items-center gap-1 text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking…</span>
                )}
                {checkState.status === 'available' && (
                  <span className="flex items-center gap-1 text-green-600"><Check className="h-3.5 w-3.5" /> {checkState.message}</span>
                )}
                {checkState.status === 'error' && (
                  <span className="flex items-center gap-1 text-destructive"><X className="h-3.5 w-3.5" /> {checkState.message}</span>
                )}
              </div>
            </div>

            <div>
              <Label>Logo <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="h-14 w-14 rounded-xl border bg-muted flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="h-full w-full object-cover" />
                  ) : (
                    <Upload className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  {logoFile ? 'Change logo' : 'Upload logo'}
                </Button>
                <input ref={fileInputRef} type="file" accept={ACCEPTED.join(',')} className="hidden" onChange={handleFileSelect} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG, SVG, or WebP up to 2MB. Uploaded after the org is created.</p>
            </div>

            <div className="border rounded-2xl p-4 space-y-4 bg-muted/30">
              <div>
                <Label className="text-sm font-semibold">Privacy mode</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  This is a core decision about how athletes and coaches are handled. It is hard to reverse later.
                </p>
              </div>

              <RadioGroup
                value={privateAthletes ? 'private' : 'open'}
                onValueChange={(v) => {
                  const isPrivate = v === 'private';
                  setPrivateAthletes(isPrivate);
                  if (isPrivate) setShowEventsOnRecruitlook(false);
                }}
                className="grid grid-cols-1 gap-3"
              >
                <label
                  htmlFor="privacy-open"
                  className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                    !privateAthletes ? 'border-primary bg-primary/5' : 'border-input hover:bg-muted/50'
                  }`}
                >
                  <RadioGroupItem value="open" id="privacy-open" className="mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Open organization</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Athletes registered to this org's events are propagated to RecruitLook profiles and can use RecruitLook's recruiting features.
                    </p>
                  </div>
                </label>

                <label
                  htmlFor="privacy-private"
                  className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${
                    privateAthletes ? 'border-primary bg-primary/5' : 'border-input hover:bg-muted/50'
                  }`}
                >
                  <RadioGroupItem value="private" id="privacy-private" className="mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Private organization</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Athletes and coaches who register stay inside this organization only. They do NOT get RecruitLook profiles and won't appear on RecruitLook. A fully self-contained, white-label tournament system.
                    </p>
                  </div>
                </label>
              </RadioGroup>

              <div className={`flex items-start gap-3 ${privateAthletes ? 'opacity-50 pointer-events-none' : ''}`}>
                <Checkbox
                  id="show-events"
                  checked={privateAthletes ? false : showEventsOnRecruitlook}
                  onCheckedChange={(checked) => setShowEventsOnRecruitlook(checked === true)}
                  disabled={privateAthletes}
                />
                <div className="grid gap-1 leading-none">
                  <Label htmlFor="show-events" className="text-sm font-normal cursor-pointer">
                    Show this org's events on RecruitLook
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {privateAthletes
                      ? 'Private organizations never surface on RecruitLook.'
                      : 'Events will be discoverable on the RecruitLook platform.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <div>
                <Label>Org admin email *</Label>
                <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="admin@acme.com" />
                <p className="text-xs text-muted-foreground mt-1">
                  Brand-new admins get an email to set a password. Existing RecruitLook users are matched by email and just accept.
                </p>
              </div>
              <div>
                <Label>Admin name <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Jane Doe" />
              </div>
              <div>
                <Label>Personal message <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Textarea value={personalMessage} onChange={(e) => setPersonalMessage(e.target.value)} rows={2} maxLength={500} placeholder="Welcome to RecruitLook Events!" />
              </div>
            </div>

            {error && (
              <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create organization
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default NewOrganizationDialog;
